import BaseWrapper from "../../components/ui/BaseWrapper";
import dayjs from "dayjs";
import BaseRating from "../../components/ui/BaseRating";
import BaseDataTableV2 from "../../components/ui/BaseDataTableV2";
import BaseTextField from "../../components/ui/BaseTextField";
import BaseButton from "../../components/ui/BaseButton";
import BasePopover from "../../components/ui/BasePopover";
import BaseRadio from "../../components/ui/BaseRadio";
import BaseAutoSelect from "../../components/ui/BaseAutoSelect";
import { getFreelanceCompanies } from "../../services/api/freelanceCompany";
import {
  addEngineerToTalentPool,
  createTalentPool,
  fetchTalentPool,
} from "../../services/api/talentPool";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useDispatch } from "react-redux";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { Avatar, RadioGroup } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { debounce } from "../../utils/helpers";
import { showToast } from "../../store/toast/toastSlice";
import useAuth from "../../hooks/useAuth";
import { ModuleType, UserType } from "../../utils/enum";

const relativeTime = require("dayjs/plugin/relativeTime");
dayjs.extend(relativeTime);

const FreelanceCompaniesTable = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { getPermissionForModule, authRole } = useAuth();
  const isCustomer = authRole === UserType.CUSTOMER;
  const { hasWriteAccess: hasTalentPoolWriteAccess } = getPermissionForModule(
    ModuleType.WORK_FORCE_TALENT_POOL,
  );
  const { hasUpdateAccess: hasWorkForceEngineerUpdateAccess } = getPermissionForModule(
    ModuleType.WORK_FORCE_ENGINEERS,
  );

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [pagination, setPagination] = useState({ page: 0, pageSize: 10 });

  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [addToTalentPoolPopoverOpen, setAddToTalentPoolPopoverOpen] = useState(null);
  const [talentPoolMode, setTalentPoolMode] = useState("");
  const [talentPoolName, setTalentPoolName] = useState("");

  const queryParams = useMemo(
    () => ({
      page: pagination.page + 1,
      limit: pagination.pageSize,
      search: debouncedSearch || "",
    }),
    [pagination.page, pagination.pageSize, debouncedSearch],
  );

  const { isPending: isLoading, data: companiesResponse } = useQuery({
    queryKey: ["freelanceCompanies", queryParams],
    queryFn: () => getFreelanceCompanies(queryParams),
    keepPreviousData: true,
    staleTime: 1000 * 60 * 5,
  });

  const companies = companiesResponse?.data;
  const companyRows = companies?.data || [];

  const { data: talentPoolData, refetch: refetchTalentPool } = useQuery({
    queryKey: ["talentPool"],
    queryFn: () => fetchTalentPool(),
    keepPreviousData: true,
  });

  const formattedTalentPoolOptions = talentPoolData?.data?.data?.map((talentPool) => ({
    label: talentPool?.name,
    value: talentPool?.id,
  }));

  const {
    isPending: isAddEngineerToTalentPoolApiLoading,
    mutateAsync: addEngineerToTalentPoolMutation,
  } = useMutation({ mutationFn: addEngineerToTalentPool });

  const { isPending: isCreateTalentPoolApiLoading, mutateAsync: createTalentPoolMutation } =
    useMutation({ mutationFn: createTalentPool });

  useEffect(() => {
    if (hasTalentPoolWriteAccess) {
      setTalentPoolMode("CREATE");
    } else if (hasWorkForceEngineerUpdateAccess) {
      setTalentPoolMode("UPDATE");
    }
  }, []);

  const selectedOwnerIds = useMemo(
    () =>
      companyRows
        .filter((v) => selectedCompanies.includes(v.id))
        .map((v) => v.ownerId)
        .filter(Boolean),
    [companyRows, selectedCompanies],
  );

  const handleClick = (event) => setAddToTalentPoolPopoverOpen(event.currentTarget);
  const handleClose = () => setAddToTalentPoolPopoverOpen(null);

  const handleAddCompaniesToTalentPool = async (talentPoolId) => {
    try {
      await addEngineerToTalentPoolMutation({
        talentPoolId,
        userIds: selectedOwnerIds,
      });
      navigate(`/workForce/talentPool/engineers/${talentPoolId}`);
      setAddToTalentPoolPopoverOpen(null);
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to add to talent pool",
          type: "error",
        }),
      );
    }
  };

  const handleCreateTalentPool = async () => {
    try {
      const newTalentPool = await createTalentPoolMutation({ name: talentPoolName });
      setTalentPoolName("");
      refetchTalentPool();
      return newTalentPool;
    } catch (error) {
      dispatch(
        showToast({
          message: error?.response?.data?.message || "Failed to create talent pool",
          type: "error",
        }),
      );
    }
  };

  const handleCreateNewTalentPoolAndAddCompanies = async () => {
    try {
      const talentPoolCreated = await handleCreateTalentPool();
      await handleAddCompaniesToTalentPool(talentPoolCreated?.data?.id);
    } catch (error) {}
  };

  const handleAddToTalentPoolButton = async () => {
    if (talentPoolMode === "UPDATE") {
      await handleAddCompaniesToTalentPool(talentPoolName);
    } else {
      await handleCreateNewTalentPoolAndAddCompanies();
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    debouncedSearchRef.current(e.target.value);
  };

  const debouncedSearchRef = useRef(
    debounce((value) => {
      setDebouncedSearch(value);
      setPagination((prev) => ({ ...prev, page: 0 }));
    }, 500),
  );

  const goToDetails = (id) => navigate(`/workForce/freelanceCompanies/${id}`);

  const columns = [
    {
      pinLeft: true,
      field: "id",
      headerName: "ID",
      width: 100,
      renderCell: (params) => (
        <div className="flex items-center w-[206px]">
          <p
            className="table-font-size font-normal text-primary cursor-pointer ml-4"
            onClick={() => goToDetails(params.row.id)}
          >
            {params.value}
          </p>
        </div>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      width: 200,
      renderCell: (params) => (
        <div
          className="flex items-center cursor-pointer"
          onClick={() => goToDetails(params.row.id)}
        >
          <Avatar src={params.row.profile} sx={{ width: 28, height: 28 }} />
          <p className="ml-3 table-font-size truncate max-w-64 overflow-hidden whitespace-nowrap text-primary capitalize">
            {params.value}
          </p>
        </div>
      ),
    },
    {
      field: "engineerCount",
      headerName: "Engineers",
      width: 120,
      renderCell: (params) => <p className="table-font-size">{params.value ?? 0}</p>,
    },
    {
      field: "workedWithJobsCount",
      headerName: "Jobs",
      width: 160,
      renderCell: (params) => (
        <div className="table-font-size">
          {/* Marketplace vs My Company split disabled — always show My Company count.
          {params.row.workedWith ? (
            <p className="text-primary font-medium">
              {params.row.workedWithJobsCount}{" "}
              <span className="text-[11px] font-300">(My Company)</span>
            </p>
          ) : (
            <p className="font-medium">
              {params.row.marketplaceJobsCount}
              <span className="text-[11px] font-300"> (Marketplace)</span>
            </p>
          )} */}
          <p className="text-primary font-medium">
            {params.row.workedWithJobsCount}{" "}
            <span className="text-[11px] font-300">(My Company)</span>
          </p>
        </div>
      ),
    },
    {
      field: "rating",
      headerName: "Rating",
      width: 180,
      renderCell: (params) => (
        <div className="flex items-center">
          <BaseRating rating={params.value} />
          <p className="ml-3">{params.value || ""}</p>
        </div>
      ),
    },
    {
      field: "cancellationRate",
      headerName: "Cancellation",
      width: 180,
      renderCell: (params) => <p className="table-font-size">{params.value}%</p>,
    },
    {
      field: "timelinessRate",
      headerName: "Timeliness",
      width: 180,
      renderCell: (params) => <p className="table-font-size">{params.value}%</p>,
    },
    {
      field: "workedWith",
      headerName: "Worked With",
      width: 180,
      renderCell: (params) => (
        <p className="table-font-size">
          {params.value ? (
            <i className="fa fa-check text-sm text-primary bg-[#D8EBF8] rounded-full w-5 text-center"></i>
          ) : (
            <i className="fa fa-xmark text-sm text-red-500 bg-[#FFCDCD] rounded-full w-5 text-center"></i>
          )}
        </p>
      ),
    },
    {
      field: "createdAt",
      headerName: "Registration Date",
      width: 160,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">
          {`${dayjs(params.value).format("DD-MM-YYYY") || ""}`}
        </p>
      ),
    },
    {
      field: "phoneNumber",
      headerName: "Mobile",
      width: 160,
      renderCell: (params) => {
        const raw = params.value;

        if (!raw) return <p>-</p>;

        const phone = parsePhoneNumberFromString(raw.startsWith("+") ? raw : `+${raw}`);

        if (!phone) {
          return <p className="table-font-size cursor-default">{raw}</p>;
        }

        const formatted = phone.formatInternational().replace(/\s+/g, "-");
        const masked = formatted.slice(0, 10) + ".....";

        return <p className="table-font-size cursor-default">{masked}</p>;
      },
    },
    {
      field: "email",
      headerName: "Email",
      width: 240,
      renderCell: (params) => <p className="table-font-size cursor-default">{params.value}</p>,
    },
    {
      field: "personalAddress",
      headerName: "Location",
      width: 220,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">
          {`${params.row?.personalAddress?.addressLine1 || ""}`}
        </p>
      ),
    },
    {
      field: "city",
      headerName: "City",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">{`${
          params.row?.personalAddress?.city || ""
        }`}</p>
      ),
    },
    {
      field: "state",
      headerName: "State",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">{`${
          params.row?.personalAddress?.state || ""
        }`}</p>
      ),
    },
    {
      field: "zipcode",
      headerName: "Zip / Postal Code",
      width: 150,
      renderCell: (params) => (
        <p className="table-font-size cursor-default">{`${
          params.row?.personalAddress?.zipCode || ""
        }`}</p>
      ),
    },
    {
      field: "country",
      headerName: "Country",
      width: 130,
      renderCell: (params) => (
        <p className="table-font-size">{params.row?.personalAddress?.country || ""}</p>
      ),
    },
    {
      field: "lastActive",
      headerName: "Last Active",
      width: 140,
      renderCell: (params) => (
        <p className="table-font-size">{params.value ? dayjs(params.value).fromNow() : ""}</p>
      ),
    },
  ];

  return (
    <BaseWrapper>
      <div className="bg-white rounded-lg px-5 py-2 shadow-md mt-2">
        <div className="flex justify-end items-center w-full">
          <div className="w-[28%]">
            <BaseTextField
              placeholder={`Search`}
              value={search}
              onChange={(e) => handleSearchChange(e)}
              startAdornment={<i className="fa-light fa-magnifying-glass text-lg"></i>}
            />
          </div>
        </div>
      </div>

      {selectedCompanies.length > 0 &&
        (hasWorkForceEngineerUpdateAccess || hasTalentPoolWriteAccess) && (
          <BaseButton
            className="bg-white text-default py-[4px] px-[24px] mt-2"
            onClick={handleClick}
          >
            Move to Talent Pool
          </BaseButton>
        )}
      <BasePopover
        open={!!addToTalentPoolPopoverOpen}
        anchorEl={addToTalentPoolPopoverOpen}
        onClose={handleClose}
      >
        <div className="w-[360px]">
          <div className="p-4">
            <h6 className="pb-1 border-b">Add to Talent Pool</h6>
            <RadioGroup
              name="talentPool"
              value={talentPoolMode}
              onChange={(e) => setTalentPoolMode(e.target.value)}
              className="text-xs"
            >
              {hasWorkForceEngineerUpdateAccess && (
                <div>
                  <BaseRadio
                    label={<p className="text-xs">Add to Existing Talent Pool</p>}
                    value="UPDATE"
                    name="talentPool"
                    sx={{ "& .MuiSvgIcon-root": { color: "#0F40AE" } }}
                  />
                </div>
              )}
              {hasTalentPoolWriteAccess && (
                <div>
                  <BaseRadio
                    label={<p className="text-xs">Create new Talent Pool</p>}
                    value="CREATE"
                    name="talentPool"
                    sx={{ "& .MuiSvgIcon-root": { color: "#0F40AE" } }}
                  />
                </div>
              )}
            </RadioGroup>

            {talentPoolMode === "UPDATE" && hasWorkForceEngineerUpdateAccess && (
              <div className="mt-2">
                <BaseAutoSelect
                  placeholder="Select Your Talent Pool"
                  label="Talent Pool"
                  isDefaultField={false}
                  labelShrink={false}
                  required={true}
                  options={formattedTalentPoolOptions}
                  onChange={(e) => setTalentPoolName(e.target.value)}
                />
              </div>
            )}
            {talentPoolMode === "CREATE" && hasTalentPoolWriteAccess && (
              <div className="mt-2">
                <BaseTextField
                  placeholder="Enter new Talent Pool name"
                  label="New Talent Pool"
                  isDefaultField={false}
                  labelShrink={false}
                  required={true}
                  optional={false}
                  value={talentPoolName}
                  onChange={(e) => setTalentPoolName(e.target.value.trim())}
                />
              </div>
            )}
          </div>
          <div
            className="flex justify-center py-4"
            style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
          >
            <BaseButton
              onClick={() => setAddToTalentPoolPopoverOpen(null)}
              color="light"
              className="py-[3px] px-[24px] bg-light text-default hover:text-primary mr-4"
            >
              Cancel
            </BaseButton>
            <BaseButton
              isLoading={isCreateTalentPoolApiLoading || isAddEngineerToTalentPoolApiLoading}
              color="primary"
              disabled={talentPoolName?.length < 1}
              className="py-[3px] px-[28px]"
              onClick={handleAddToTalentPoolButton}
            >
              Save
            </BaseButton>
          </div>
        </div>
      </BasePopover>

      <div className="mt-2">
        <BaseDataTableV2
          className="max-h-[calc(100vh-300px)]"
          rowHeight={38}
          columns={
            isCustomer
              ? columns.filter((c) => !["phoneNumber", "email"].includes(c.field))
              : columns
          }
          data={companies?.data || []}
          isLoading={isLoading}
          disableColumnResize={false}
          disableRowSelectionOnClick
          checkboxSelection
          onRowSelectionModelChange={(companyIds) => setSelectedCompanies(companyIds)}
          hideFooter={false}
          pagination
          rowCount={companies?.totalCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          paginationMode="server"
        />
      </div>
    </BaseWrapper>
  );
};

export default FreelanceCompaniesTable;
